import { useState, useCallback } from "react";

import { Users, Check, Trash2, Globe, MapPin, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { toast } from "sonner";

import { cn } from "@/modern/utils";

import { useFileDetailModal } from "../../../context/FileDetailModalContext";
import { SearchTextLink } from "../SearchTextLink";
import { ExtractedClient } from "../types";

interface ClientExtractionResultsProps {
  clients: ExtractedClient[];
}

interface ClientCardState {
  dismissed: boolean;
  applied: boolean;
  processing: boolean;
}

export const ClientExtractionResults = ({ clients }: ClientExtractionResultsProps) => {
  const { project, entityRecord } = useFileDetailModal();

  const [cardStates, setCardStates] = useState<Record<number, ClientCardState>>(() => {
    const initial: Record<number, ClientCardState> = {};
    clients.forEach((_, index) => {
      initial[index] = { dismissed: false, applied: false, processing: false };
    });
    return initial;
  });

  const updateCardState = useCallback((index: number, update: Partial<ClientCardState>) => {
    setCardStates((prev) => ({
      ...prev,
      [index]: { ...prev[index], ...update },
    }));
  }, []);

  const handleLinkClient = useCallback(async (client: ExtractedClient, index: number) => {
    if (!project || !entityRecord) return;

    const state = cardStates[index];
    if (state?.applied || state?.processing) return;

    updateCardState(index, { processing: true });

    try {
      const clientsStore = (project as any).clientsStore;
      if (!clientsStore) {
        throw new Error("Client store not available");
      }

      const clientRecord = clientsStore.createRecord({
        name: client.name,
        website: client.website || "",
        address: client.address || "",
      });

      clientsStore.add(clientRecord);
      await project.sync();

      (entityRecord as any).clientId = clientRecord.id;
      await project.sync();

      updateCardState(index, { applied: true, processing: false });
      toast.success("Client Created", {
        description: `"${client.name}" has been created and linked.`,
      });
    } catch (error) {
      console.error("[ClientExtractionResults] Failed to create client:", error);
      updateCardState(index, { processing: false });
      toast.error("Failed to Create Client", {
        description: "Something went wrong. Please try again.",
      });
    }
  }, [project, entityRecord, cardStates, updateCardState]);

  const handleDismiss = useCallback((index: number) => {
    updateCardState(index, { dismissed: true });
  }, [updateCardState]);

  const visibleClients = clients.filter((_, index) => !cardStates[index]?.dismissed);

  if (visibleClients.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <p className="text-sm text-muted-foreground">All client cards have been dismissed.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3">
      <AnimatePresence>
        {clients.map((client, index) => {
          const state = cardStates[index];
          if (state?.dismissed) return null;

          return (
            <motion.div
              key={index}
              layout
              initial={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className={cn(
                "border border-border rounded-swivu-base overflow-hidden",
                state?.applied && "border-green-500/30 bg-green-500/5"
              )}
            >
              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className="text-sm font-medium text-foreground">
                      {client.name}
                    </span>

                    {client.website && (
                      <div className="flex items-center gap-1 mt-1">
                        <Globe className="w-3 h-3 text-muted-foreground shrink-0" />
                        <a
                          href={client.website.startsWith("http") ? client.website : `https://${client.website}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline truncate"
                        >
                          {client.website}
                        </a>
                      </div>
                    )}

                    {client.address && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground line-clamp-2">
                          {client.address}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Action icons */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      title="Create & Link Client"
                      disabled={state?.applied}
                      onClick={() => handleLinkClient(client, index)}
                      className={cn(
                        "relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-150",
                        "hover:bg-primary/10 hover:text-primary active:bg-primary/20",
                        state?.applied && "text-green-600 dark:text-green-400",
                        state?.processing && "bg-primary text-primary-foreground hover:!bg-primary hover:!text-primary-foreground"
                      )}
                    >
                      {state?.applied ? (
                        <Check className="w-3.5 h-3.5" />
                      ) : (
                        state?.processing ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Users className="w-3.5 h-3.5" />
                        )
                      )}
                    </button>
                    <button
                      type="button"
                      title="Dismiss"
                      onClick={() => handleDismiss(index)}
                      className={cn(
                        "relative flex items-center justify-center w-7 h-7 rounded-md transition-all duration-150",
                        "hover:bg-destructive/10 hover:text-destructive active:bg-destructive/20",
                        state?.applied && "opacity-40 pointer-events-none"
                      )}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {client.search_text && (
                  <div className="mt-2">
                    <SearchTextLink searchText={client.search_text} />
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
